import { useEffect, useState } from 'react'
import { ArrowRight, Check, Cloud, Loader2, Monitor, Store } from 'lucide-react'
import clsx from 'clsx'

import { api, ErrorApi, mensajeDeError } from '@/lib/api'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'

// El primer arranque de la caja del mostrador.
//
// Sin esta pantalla, estrenar una caja era editar el `appsettings.json` con el
// Notepad y después mandarle dos POST a mano al host local: los endpoints
// estaban completos desde el principio y NO los llamaba nadie —el mismo error
// que la autorización con PIN y la tecla rápida del medio de pago, backend
// entero y cero pantalla—. Quien instala esto es el verdulero, no un técnico.
//
// Son tres pasos y cada uno se verifica antes de pasar al siguiente, que es lo
// que hace que un error se vea DONDE está: la dirección se prueba contra el
// /health de la nube, las credenciales se prueban trayendo las cajas del
// local, y recién ahí se elige cuál es esta máquina. Con un solo formulario,
// un typo en la dirección salía como «usuario o contraseña incorrectos».

interface Estado {
  registrada: boolean
  nubeUrl: string
  nubeDeFabrica: string
}

interface CajaDisponible {
  id: number
  code: string
  nombre: string
  prefijo: string
  tipo: string

  /** Ya hay otra máquina registrada en esa caja. No lo prohíbe, lo avisa. */
  registrado: boolean
}

/**
 * @param mudando Cuando la caja YA estaba registrada y se la está cambiando de
 * local, de nube o de punto de venta. Cambia los textos y agrega el aviso: no
 * es lo mismo estrenar una caja que mudar una que estuvo vendiendo, y quien
 * llega acá por el segundo camino tiene que saber qué se lleva puesto.
 * @param onCancelar Para poder salir sin cambiar nada. En el primer arranque
 * no existe —no hay a dónde volver—, y mudando sí.
 */
export function AsistenteRegistro({
  onListo,
  mudando,
  onCancelar,
}: {
  onListo: () => void
  mudando?: boolean
  onCancelar?: () => void
}) {
  useAyudaDe('caja.registro')

  const [paso, setPaso] = useState<1 | 2 | 3>(1)
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const [url, setUrl] = useState('')
  const [slug, setSlug] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [cajas, setCajas] = useState<CajaDisponible[]>([])
  const [elegida, setElegida] = useState<number | null>(null)
  // Los DOS nombres: el del local y el de la caja. El paso final decía «quedó
  // registrada en Caja 1» —el nombre de la CAJA en el lugar del local—, que es
  // justo el dato que alguien vino a verificar cuando estrena la segunda
  // máquina del mostrador.
  const [registrada, setRegistrada] = useState<{ local: string; caja: string } | null>(null)

  // La dirección que ya tiene configurada, para no hacer tipear lo que el
  // instalador dejó en el appsettings.
  useEffect(() => {
    api
      .get<Estado>('/registro')
      .then((e) => setUrl(e.nubeUrl))
      .catch(() => {
        /* Si no responde, se escribe a mano: no hay nada que avisar todavía. */
      })
  }, [])

  const conError = async (accion: () => Promise<void>) => {
    setEnviando(true)
    setError(null)

    try {
      await accion()
    } catch (e) {
      // El mensaje del host local viene en `message` y es el que hay que
      // mostrar: dice cuál de los tres datos está mal.
      setError(mensajeDeError(e, 'No se pudo completar el paso.'))
    } finally {
      setEnviando(false)
    }
  }

  const guardarDireccion = () =>
    conError(async () => {
      await api.post('/registro/nube', { url })
      setPaso(2)
    })

  const traerCajas = () =>
    conError(async () => {
      const lista = await api.post<CajaDisponible[]>('/registro/cajas', { slug, email, password })

      if (lista.length === 0) {
        throw new ErrorApi(
          'Ese local no tiene ninguna caja cargada. Se crean desde Configuración → Cajas, ' +
            'en el navegador.',
          400,
        )
      }

      setCajas(lista)

      // Con una sola caja disponible no hay nada que elegir, pero igual se
      // muestra el paso: es la última pantalla antes de atar esta PC a un
      // punto de venta, y conviene que la persona vea cuál.
      setElegida(lista.find((c) => !c.registrado)?.id ?? lista[0]?.id ?? null)
      setPaso(3)
    })

  const registrar = () =>
    conError(async () => {
      const r = await api.post<{ local: string; caja: string }>('/registro', {
        slug,
        email,
        password,
        puntoDeVentaId: elegida,
      })

      setRegistrada(r)
    })

  if (registrada !== null) {
    return (
      <Marco titulo="Listo" paso={3}>
        <div className="text-center">
          <span className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-verde-tenue">
            <Check className="h-6 w-6 text-verde-oscuro" />
          </span>

          <p className="text-[15px] font-semibold text-tinta">
            Esta máquina quedó registrada como {registrada.caja}, en {registrada.local}.
          </p>

          <p className="mt-2 text-[13.5px] leading-relaxed text-tinta-tenue">
            Ya baja el catálogo y los precios sola, y desde ahora se entra con el PIN de cada
            empleado —también sin internet—.
          </p>

          <button type="button" onClick={onListo} className="btn-primario mt-6 w-full">
            Empezar a usarla
          </button>
        </div>
      </Marco>
    )
  }

  return (
    <Marco titulo={mudando ? 'Cambiar el local o la caja' : 'Conectar esta caja'} paso={paso}>
      {paso === 1 && (
        <>
          <Encabezado
            icono={<Cloud className="h-5 w-5 text-verde-oscuro" />}
            titulo="¿Dónde está tu sistema?"
            detalle="La dirección de Fruver en internet, la misma que usás en la computadora del negocio."
          />

          {/* Mudando hay que decir qué se lleva puesto y qué no: es una caja
              que estuvo vendiendo, no una recién instalada. Lo que queda en la
              base local —las ventas ya subidas, los turnos— no se toca; lo que
              cambia es a qué local pertenece de acá en adelante. */}
          {mudando && (
            <p className="mb-3 rounded-lg bg-ambar/10 px-3 py-2.5 text-[12.5px] leading-relaxed text-ambar">
              Esta caja ya está registrada. Al terminar va a quedar atada al local y a la caja que
              elijas ahora, y el talonario sigue el de esa caja. Solo se puede si no queda nada
              sin subir.
            </p>
          )}

          <label className="block">
            <span className="etiqueta">Dirección</span>
            <input
              className="campo"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://api.tu-verduleria.com"
              autoComplete="off"
              spellCheck={false}
              inputMode="url"
            />
          </label>

          <p className="mt-1.5 text-[12.5px] leading-relaxed text-tinta-tenue">
            Se prueba antes de guardar, así que si está mal escrita te lo decimos acá y no tres
            pasos más adelante.
          </p>

          <Aviso error={error} />

          <button
            type="button"
            onClick={() => void guardarDireccion()}
            disabled={enviando || url.trim() === ''}
            className="btn-primario mt-5 w-full"
          >
            {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Probar y seguir <ArrowRight className="h-4 w-4" /></>}
          </button>

          {onCancelar && (
            <button type="button" onClick={onCancelar} className="btn-secundario mt-2 w-full">
              Dejar todo como está
            </button>
          )}
        </>
      )}

      {paso === 2 && (
        <>
          <Encabezado
            icono={<Store className="h-5 w-5 text-verde-oscuro" />}
            titulo="¿De qué local es esta caja?"
            detalle="Con el usuario del dueño o de quien administra: se usa una sola vez, para dar de alta la caja."
          />

          <div className="space-y-3">
            <label className="block">
              <span className="etiqueta">Local</span>
              <input
                className="campo"
                value={slug}
                onChange={(e) => setSlug(e.target.value.trim().toLowerCase())}
                placeholder="laesquina"
                autoComplete="off"
                spellCheck={false}
              />
              <span className="mt-1 block text-[12.5px] text-tinta-tenue">
                Es el nombre corto que aparece en la dirección del sistema.
              </span>
            </label>

            <label className="block">
              <span className="etiqueta">Correo</span>
              <input
                className="campo"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                autoComplete="username"
              />
            </label>

            <label className="block">
              <span className="etiqueta">Contraseña</span>
              <input
                className="campo"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !enviando) void traerCajas()
                }}
              />
            </label>
          </div>

          <Aviso error={error} />

          <div className="mt-5 flex gap-2">
            <button type="button" onClick={() => setPaso(1)} className="btn-secundario">
              Volver
            </button>

            <button
              type="button"
              onClick={() => void traerCajas()}
              disabled={enviando || slug === '' || email === '' || password === ''}
              className="btn-primario flex-1"
            >
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Buscar las cajas <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        </>
      )}

      {paso === 3 && (
        <>
          <Encabezado
            icono={<Monitor className="h-5 w-5 text-verde-oscuro" />}
            titulo="¿Cuál es esta máquina?"
            detalle="Cada caja lleva su propio talonario y su propio arqueo, así que esta PC tiene que ser una sola de la lista."
          />

          <div className="space-y-2">
            {cajas.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setElegida(c.id)}
                className={clsx(
                  'flex w-full items-center justify-between gap-3 rounded-xl border px-3.5 py-3 text-left transition',
                  elegida === c.id
                    ? 'border-verde bg-verde-tenue'
                    : 'border-borde bg-papel hover:border-borde-fuerte',
                )}
              >
                <span>
                  <span className="block text-[14px] font-semibold text-tinta">{c.nombre}</span>
                  <span className="block text-[12.5px] text-tinta-tenue">
                    Comprobantes {c.prefijo}-000001 · {c.tipo}
                  </span>
                </span>

                {/* Que otra máquina ya esté usando esa caja no lo prohíbe el
                    sistema —una PC que se reemplaza vuelve a registrarse en la
                    misma— pero hay que decirlo: dos máquinas en la misma caja
                    comparten talonario y arqueo. */}
                {c.registrado && (
                  <span className="shrink-0 rounded-full bg-ambar/15 px-2 py-0.5 text-[11.5px] font-semibold text-ambar">
                    ya en uso
                  </span>
                )}
              </button>
            ))}
          </div>

          <Aviso error={error} />

          <div className="mt-5 flex gap-2">
            <button type="button" onClick={() => setPaso(2)} className="btn-secundario">
              Volver
            </button>

            <button
              type="button"
              onClick={() => void registrar()}
              disabled={enviando || elegida === null}
              className="btn-primario flex-1"
            >
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar esta caja'}
            </button>
          </div>
        </>
      )}
    </Marco>
  )
}

function Marco({
  titulo,
  paso,
  children,
}: {
  titulo: string
  paso: 1 | 2 | 3
  children: React.ReactNode
}) {
  return (
    <div className="grid min-h-screen place-items-center bg-crema px-4 py-8">
      <div className="w-full max-w-[420px]">
        <h1 className="mb-1 text-center text-[22px] font-bold tracking-[-0.02em] text-tinta">
          {titulo}
        </h1>

        <p className="mb-5 text-center text-[13px] text-tinta-tenue">Paso {paso} de 3</p>

        <div className="carta p-5">{children}</div>
      </div>
    </div>
  )
}

function Encabezado({
  icono,
  titulo,
  detalle,
}: {
  icono: React.ReactNode
  titulo: string
  detalle: string
}) {
  return (
    <div className="mb-4 flex items-start gap-3">
      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-verde-tenue">
        {icono}
      </span>

      <span>
        <span className="block text-[15px] font-semibold text-tinta">{titulo}</span>
        <span className="mt-0.5 block text-[12.5px] leading-relaxed text-tinta-tenue">
          {detalle}
        </span>
      </span>
    </div>
  )
}

function Aviso({ error }: { error: string | null }) {
  if (error === null) return null

  return (
    <p className="mt-4 rounded-lg bg-rojo/[0.07] px-3 py-2.5 text-[13px] leading-relaxed text-rojo">
      {error}
    </p>
  )
}
